import React from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";

type ErrorBoundaryProps = {
	children: React.ReactNode;
};

type ErrorBoundaryState = {
	error: Error | undefined;
};

/**
 * Catches render-phase throws so a single bad component does not blank the whole SPA.
 * Only render/lifecycle errors are caught here, async rejections still need their own try/catch.
 */
export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { error: undefined };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { error };
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		console.error("Unhandled render error:", error, info.componentStack);
	}

	handleReset = () => {
		this.setState({ error: undefined });
	};

	handleReload = () => {
		window.location.href = "/";
	};

	render(): React.ReactNode {
		if (!this.state.error) return this.props.children;

		return (
			<Row className="mt-3">
				<Col></Col>
				<Col xs={9}>
					<Alert variant="danger">
						<Alert.Heading>Something went wrong</Alert.Heading>
						<p className="mb-0">
							The page could not be displayed. You can try again, or reload the application. If the problem persists please
							contact us at support@businessmap.io
						</p>
						{this.state.error.message && <p className="mb-0 mt-2 text-muted">{this.state.error.message}</p>}
						<hr />
						<Button variant="primary" className="me-2" onClick={this.handleReset}>
							Try again
						</Button>
						<Button variant="secondary" onClick={this.handleReload}>
							Reload the application
						</Button>
					</Alert>
				</Col>
				<Col></Col>
			</Row>
		);
	}
}
